import { useTranslations } from '@vocab/react';
import { Stack, Divider, Box, Text } from 'braid-design-system';
import { useState } from 'react';

import { handleAuthCheck } from 'src/core/utils/authUtil';
import type { CategoryGroupsLoaderData } from 'src/mapper/groupMapper';
import logger from 'src/modules/logger/logger';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useGroup } from 'src/provider/group';
import { useLoginModal } from 'src/provider/loginModal';
import { usePage } from 'src/provider/page';
import {
  updateFollowCategoryStatus,
  type updateFollowCategoryStatusParams,
} from 'src/services/categoriesService';

import { FollowButton } from '../Buttons';

import translations from './.vocab';

interface GroupCardState {
  group: CategoryGroupsLoaderData;
}

export default ({ group }: GroupCardState) => {
  const { pageDispatch } = usePage();
  const { authStatus } = useAuthStatus();
  const { openLoginDialog } = useLoginModal();
  const { trackAnalyticsInteraction } = useAnalytics();
  const { groupDispatch } = useGroup();
  const {
    categoryId,
    groupId,
    name: categoryName,
    description,
    memberCount,
    followed,
    url,
  } = group;
  const followerLabel = memberCount.label;
  const { t } = useTranslations(translations);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFollow = async () => {
    try {
      setIsLoading(true);
      if (
        !handleAuthCheck({
          authStatus,
          openLoginDialog,
          trackAnalyticsInteraction,
        })
      )
        return;
      const params: updateFollowCategoryStatusParams = {
        groupId,
        followed: !followed,
      };
      await updateFollowCategoryStatus(params);
      groupDispatch({
        type: 'UPDATE_FOLLOW_STATUS',
        payload: { selectedCategoryId: categoryId, status: !followed },
      });
      setIsLoading(false);
    } catch (err) {
      logger.error({ err }, 'handleFollow');
      // Do noting
    }
  };

  return (
    <Stack space="small">
      <Box display="flex" justifyContent="spaceBetween">
        <Box style={{ width: '-webkit-fill-available' }} paddingBottom="xsmall">
          <a
            key={categoryId}
            href={url}
            style={{ textDecoration: 'none' }}
            onClick={() => {
              pageDispatch({ type: 'IS_LOADING' });
            }}
          >
            <Stack space="xsmall">
              <Text
                align="left"
                size="standard"
                tone="neutral"
                weight="medium"
                data={{ testid: 'category-name' }}
              >
                {categoryName}
              </Text>
              {Boolean(followerLabel) && Boolean(categoryName) && (
                <Text align="left" size="small" tone="secondary">
                  {followerLabel} {t('followers')}
                </Text>
              )}
              <Text align="left" size="small" tone="secondary">
                {description}
              </Text>
            </Stack>
          </a>
        </Box>
        <FollowButton
          onClick={handleFollow}
          followStatus={followed}
          isLoading={isLoading}
        />
      </Box>
      <Divider />
    </Stack>
  );
};
